import isEmpty from 'lodash/isEmpty'
import groupBy from 'lodash/groupBy'
import uniqBy from 'lodash/uniqBy'
import head from 'lodash/head'
import last from 'lodash/last'
import wcs from '../wcs'
import { WcsLocales } from '@/scripts/libs/wcs/types/locales'

const { langId, limitedQuantityThreshold, locale, storeId } = wcs.getStoreConfig()

import { UPS } from '@/scripts/constants/store'
import { HHmm, YYYY_MM_DD_LOWER } from '@/scripts/constants/time'

import { YES, ONE } from '@/scripts/constants/booleans'

import {
  DAYS_OF_WEEK_EN_LABEL_IN_OPENING_HOURS,
  DAYS_OF_WEEK_FR_CANADA_LABEL_IN_OPENING_HOURS,
  DAYS_OF_WEEK_ITALY_LABEL_IN_OPENING_HOURS,
  DAYS_OF_WEEK_SPAIN_LABEL_IN_OPENING_HOURS,
  SHIP_TO_NAME_1,
  SHIP_TO_STRAS,
  SHIP_TO_ORT_1,
  SHIPPING_ID,
  STOCK_AVL,
  STORE_BRAND,
  DISTRIBUTION_CHANNEL,
  OMNI_LIMITED_INV_TRESHHOLD,
  BOPSCNC,
  BOSSSTS,
  FINDSTORE,
  SUNGLASS_HUT,
  STORE_CLOSING_HOUR_THRESHOLD,
  MARKER_RED_URL,
  MARKER_BLACK_URL,
  MARKER_GREY_URL,
  TIME_ZONE,
  DAYS_OF_WEEK,
  MARKER_BLACK_SGH,
  MARKER_BLACK_LENS_CRAFTERS,
  MARKER_UPS,
} from '@/scripts/constants/physical-stores'

import { RAYBAN_STORES, CERTIFIED, PREMIUM } from '@/scripts/constants/store-locator'

import { MOBILE_PANELS_OPEN } from '@/scripts/constants/css-classes'

import { WcsPhysicalStores } from './wcs/physical-stores'
import {
  DistributionChannel,
  PhysicalStoreType,
  SfsDeliveryOptionType,
} from '@/scripts/libs/physical-stores/types/enums'
import {
  PhysicalStore,
  PhysicalStoreUserAddress,
  ShipFromStoreOption,
} from '@/scripts/libs/physical-stores/types/interfaces'
import {
  WcsEUStore,
  WcsPhysicalStore,
  WcsPhysicalStoreAttribute,
  WcsUPSStore,
} from '@/scripts/libs/physical-stores/types/wcs-interfaces'
import PhysicalStoresEU from '@/scripts/libs/physical-stores/physical-stores-eu'
import RbDate from '@/utils/rb-date'

export class PhysicalStores {
  static normalizePhysicalStore = (
    store: WcsPhysicalStore,
    searchType = PhysicalStoreType.CLICK_AND_COLLECT,
    baseUrl = ''
  ): PhysicalStore => {
    const address = store?.address ?? ''
    const storeAttributes = store?.attributes ?? []
    // TAKE NOTE THAT groupByWeekDays WILL ALTER THE ACTUAL hoursArray
    const hoursArray = PhysicalStores.setHoursToArray(store?.attributes)
    const timeZone = PhysicalStores.getObjectFromArrayByName(storeAttributes, TIME_ZONE)?.value
    const openingHours = PhysicalStores.getTimeOpen(hoursArray[0])
    const closingHours = PhysicalStores.getTimeClose(hoursArray[1])
    const availableStock = PhysicalStores.getMinimumAvailableStock(storeAttributes, STOCK_AVL)
    const omniLimitedInvThreshold = PhysicalStores.getObjectFromArrayByName(
      storeAttributes,
      OMNI_LIMITED_INV_TRESHHOLD
    )?.value
    const city = store?.city
    const country = store?.country
    const distance = store?.distance
    const distributionChannel = PhysicalStores.getObjectFromArrayByName(
      storeAttributes,
      DISTRIBUTION_CHANNEL
    )?.value
    const storeBrand = PhysicalStores.getObjectFromArrayByName(storeAttributes, STORE_BRAND)?.value
    const externalIdentifier = store?.externalIdentifier
    const groupedWeekDays = PhysicalStores.groupByWeekDays(hoursArray[0], hoursArray[1])
    const isAvailableToday =
      (timeZone &&
        PhysicalStores.isStoreOpen(
          openingHours,
          closingHours,
          timeZone,
          STORE_CLOSING_HOUR_THRESHOLD
        )) ||
      (timeZone && PhysicalStores.isStoreAboutToOpen(openingHours, timeZone))
    const isMinimumStockGuaranteed =
      !!availableStock &&
      !!omniLimitedInvThreshold &&
      availableStock > parseFloat(omniLimitedInvThreshold)
    const displayedAvailableStock = omniLimitedInvThreshold
      ? availableStock - parseFloat(omniLimitedInvThreshold)
      : 0
    const isLimitedQuantity =
      !!omniLimitedInvThreshold && displayedAvailableStock <= limitedQuantityThreshold
    const isStockAvailable = !!availableStock && isMinimumStockGuaranteed
    const isBops = PhysicalStores.isStoreBopsOrBossByType(store, BOPSCNC) && isStockAvailable
    const isBoss = PhysicalStores.isStoreBopsOrBossByType(store, BOSSSTS)
    const isFindInStore = PhysicalStores.isStoreBopsOrBossByType(store, FINDSTORE)

    const isOpen = PhysicalStores.isStoreOpened(openingHours, closingHours, timeZone ?? '')
    const latitude = store?.latitude
    const longitude = store?.longitude
    const postalCode = store?.postalCode
    const shippingId = PhysicalStores.getObjectFromArrayByName(storeAttributes, SHIPPING_ID)?.value
    const shipToName1 = PhysicalStores.getObjectFromArrayByName(
      storeAttributes,
      SHIP_TO_NAME_1
    )?.value
    const shipToOrt1 = PhysicalStores.getObjectFromArrayByName(
      storeAttributes,
      SHIP_TO_ORT_1
    )?.value
    const shipToStras = PhysicalStores.getObjectFromArrayByName(
      storeAttributes,
      SHIP_TO_STRAS
    )?.value
    const state = store?.state ?? ''
    const storeName = store?.storeName
    const telephone = store?.telephone
    const uniqueID = store?.uniqueID
    const weekScheduleTable = PhysicalStores.getWeekScheduleTable(groupedWeekDays)
    const bestCallingTime = PhysicalStores.buildBestCallingTime(groupedWeekDays)
    const weekScheduleArray = PhysicalStores.getWeekScheduleArray(storeAttributes)
    const storeType =
      searchType === PhysicalStoreType.CLICK_AND_COLLECT ? distributionChannel : store?.type
    const markerIcon = PhysicalStores.getMarkerIcon(searchType, storeType, baseUrl)
    const hereYouCanFind = store?.assortment?.split(',')
    const services = store?.services
      ?.map((service) => {
        return { name: service.name || service.Name, url: service.url || service.Url }
      })
      .filter((service) => !!service.name && !!service.url)

    const sfsOptions =
      searchType === PhysicalStoreType.SHIP_FROM_STORE
        ? PhysicalStores.getSFSOptions(storeAttributes)
        : undefined
    return {
      address,
      availableStock,
      city,
      closingHours,
      country,
      displayedAvailableStock,
      distance,
      distributionChannel,
      externalIdentifier,
      hereYouCanFind,
      hoursArray,
      isAvailableToday,
      isBops,
      isBoss,
      isFindInStore,
      isLimitedQuantity,
      isOpen,
      isStockAvailable,
      latitude,
      longitude,
      markerIcon,
      omniLimitedInvThreshold,
      openingHours,
      postalCode,
      services,
      sfsOptions,
      shippingId,
      shipToName1,
      shipToOrt1,
      shipToStras,
      state,
      storeBrand,
      storeName,
      storeSubmitted: false,
      storeType,
      telephone,
      timeZone,
      uniqueID,
      weekScheduleArray,
      weekScheduleTable,
      bestCallingTime,
    } as PhysicalStore
  }

  static getObjectFromArrayByName = (array: WcsPhysicalStoreAttribute[], name: string) => {
    if (array) {
      const found = array.find((obj) => obj.name === name)
      return found ? found : { value: '' }
    }
  }

  static getMinimumAvailableStock = (store: WcsPhysicalStoreAttribute[], attributeName: string) => {
    if (store) {
      const productsAvailability = store
        .filter((obj) => obj.name === attributeName)
        .map((productStock) => +String(productStock?.value) || 0)
      if (productsAvailability.length) {
        return Math.min(...productsAvailability)
      }
    }
    return 0
  }

  private static timeConverting = function (time: any) {
    if (time == undefined || time == null) {
      return ''
    }
    time = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [time]

    if (time.length > 1) {
      time = time.slice(1)
      time[5] = +time[0] < 12 ? ' AM' : ' PM'
      time[0] = +time[0] % 12 || 12
    }
    return time.join('')
  }

  private static buildBestCallingTime = function (hoursObject: any) {
    const bestCallingTime = []

    for (let i = 0; i < Object.keys(hoursObject).length; i++) {
      const openCloseHour =
        PhysicalStores.timeConverting(hoursObject[i][0]) +
        ' - ' +
        PhysicalStores.timeConverting(hoursObject[i][1])
      bestCallingTime.push(hoursObject[i][2])
      bestCallingTime.push(openCloseHour)
    }
    return bestCallingTime.join('+').replace(/ /g, '+').replace(/:\s*/g, '%3A')
  }

  private static daysGroupConverter = function (obj: any[]) {
    const weekMap: string[] = []
    const objConverted: string[] = []
    let moreThanOne: boolean

    const weekLabelsByCountry = PhysicalStores.getWeekLabelsByCountry()

    weekMap[0] = weekLabelsByCountry[0]
    weekMap[1] = weekLabelsByCountry[1]
    weekMap[2] = weekLabelsByCountry[2]
    weekMap[3] = weekLabelsByCountry[3]
    weekMap[4] = weekLabelsByCountry[4]
    weekMap[5] = weekLabelsByCountry[5]
    weekMap[6] = weekLabelsByCountry[6]

    obj.forEach((e, i) => {
      let prev = ''
      const eSplitted = e.split(';')
      objConverted[i] = weekMap[eSplitted[0]]

      const eSplittedLen = eSplitted.length

      if (eSplittedLen === 1) {
        objConverted[i] += ': '
      }

      if (eSplittedLen > 1) {
        for (let c = 1; c < eSplitted.length; c++) {
          if (eSplitted[c] - 1 === +eSplitted[c - 1]) {
            prev = weekMap[eSplitted[c]]
            moreThanOne = true
          } else {
            moreThanOne = false
            if (prev === '') {
              objConverted[i] += ', ' + weekMap[eSplitted[c]]
            } else {
              objConverted[i] += '-' + prev

              if (c !== eSplitted.length - 1) {
                objConverted[i] += ', '
              }
            }

            if (
              objConverted[i].charAt(objConverted[i].length - 2) !== ',' &&
              objConverted[i].charAt(objConverted[i].length - 1) !== '-'
            ) {
              objConverted[i] += ', '
            }

            objConverted[i] += weekMap[eSplitted[c]]
            prev = ''
          }
        }

        if (moreThanOne) {
          objConverted[i] += '-' + prev
          objConverted[i] += ': '
        }
      }
    })
    return objConverted
  }

  private static getWeekLabelsByCountry = function () {
    switch (locale) {
      case WcsLocales.IT_IT:
        return DAYS_OF_WEEK_ITALY_LABEL_IN_OPENING_HOURS
      case WcsLocales.ES_ES:
        return DAYS_OF_WEEK_SPAIN_LABEL_IN_OPENING_HOURS
      case WcsLocales.FR_CA:
        return DAYS_OF_WEEK_FR_CANADA_LABEL_IN_OPENING_HOURS
      default:
        return DAYS_OF_WEEK_EN_LABEL_IN_OPENING_HOURS
    }
  }

  private static groupByWeekDays = function (openTime: string[], closeTime: string[]) {
    const objHours: any = {}
    const groups = []
    const v = ['0', '1', '2', '3', '4', '5', '6']

    for (let i = 0; i < openTime.length; i++) {
      groups[i] = v[i]
      for (let j = i + 1, el = parseInt(v[j]); j < openTime.length + 1; j++, el++) {
        if (
          openTime[i] &&
          openTime[j] &&
          closeTime[i] &&
          closeTime[j] &&
          openTime[i] === openTime[j] &&
          closeTime[i] === closeTime[j]
        ) {
          groups[i] += ';' + el.toString()
          openTime.splice(j, 1)
          closeTime.splice(j, 1)
          v.splice(j, 1)
          j--
        }
      }
    }

    PhysicalStores.daysGroupConverter(groups).forEach((el, index) => {
      objHours[index] = [openTime[index], closeTime[index], el]
    })

    return objHours
  }

  static setHoursToArray = function (storeAttribute: WcsPhysicalStoreAttribute[]) {
    const weekArr: string[] = []
    const weekArrClosed: string[] = []
    const arrayOpenHour: string[] = []
    const arrayClosedHour: string[] = []

    weekArr[0] = 'openMonday'
    weekArr[1] = 'openTuesday'
    weekArr[2] = 'openWednesday'
    weekArr[3] = 'openThursday'
    weekArr[4] = 'openFriday'
    weekArr[5] = 'openSaturday'
    weekArr[6] = 'openSunday'

    weekArrClosed[0] = 'closeMonday'
    weekArrClosed[1] = 'closeTuesday'
    weekArrClosed[2] = 'closeWednesday'
    weekArrClosed[3] = 'closeThursday'
    weekArrClosed[4] = 'closeFriday'
    weekArrClosed[5] = 'closeSaturday'
    weekArrClosed[6] = 'closeSunday'

    if (storeAttribute !== undefined) {
      storeAttribute.forEach((attr) => {
        function checkOpen(w: string, i: number) {
          if (attr.value && w === attr.name) {
            arrayOpenHour[i] = attr.value
          }
        }

        function checkClosed(w: string, i: number) {
          if (attr.value && w === attr.name) {
            arrayClosedHour[i] = attr.value
          }
        }

        weekArr.find(checkOpen)
        weekArrClosed.find(checkClosed)
      })
    }

    return [arrayOpenHour, arrayClosedHour]
  }

  static writeInLocalStorageSelectedStore = function (
    catentryId: number,
    selectedStore: PhysicalStore,
    userAddress: PhysicalStoreUserAddress | undefined = undefined
  ) {
    const objPickup = {
      selected: true,
      idProduct: catentryId,
      store: selectedStore,
    }

    const organizationName = objPickup?.store?.shipToName1

    objPickup.store.organizationName = organizationName
    objPickup.store.address1 = objPickup?.store?.shipToStras

    if (userAddress) {
      objPickup.store = { ...objPickup.store, ...userAddress }
    }

    const keyRaybanPickup = PhysicalStores.getPickupStoreKey()
    localStorage.setItem(keyRaybanPickup, JSON.stringify(objPickup))
  }
  static manageFindInStoreSelection = (
    response: any,
    isCnCEnabled: boolean,
    catentryId: number,
    store: PhysicalStore,
    $mq: any
  ) => {
    if (isCnCEnabled && !isEmpty(store) && response && response.orderId[0]) {
      const orderId = response.orderId[0]
      PhysicalStores.writeInLocalStorageSelectedStore(catentryId, store)
      WcsPhysicalStores.chooseStoreAndPerformShippingModeUpdate(store, orderId, $mq, false, false)
    }
  }

  private static getWeekScheduleTable = function (hoursObject: any) {
    let weekScheduler = ''
    if (isEmpty(hoursObject)) {
      return
    }
    for (let i = 0; i < Object.keys(hoursObject).length; i++) {
      const openCloseHour =
        PhysicalStores.timeConverting(hoursObject[i][0]) +
        ' - ' +
        PhysicalStores.timeConverting(hoursObject[i][1])
      weekScheduler += `
          <p class="rb-store-details__text">
            <span>${hoursObject[i][2]}</span>
            <span>${openCloseHour}</span>
          </p>
        `
    }
    return weekScheduler
  }

  private static getFormattedDayName = (dayName: string) => dayName.toUpperCase().substring(0, 3)

  private static getWeekScheduleArray = (storeAttributes: WcsPhysicalStoreAttribute[]) => {
    const daysOfWeekWithStoreHours = DAYS_OF_WEEK.map((dayOfWeek) => {
      const openHour = PhysicalStores.getObjectFromArrayByName(
        storeAttributes,
        `open${dayOfWeek}`
      )?.value
      const closeHour = PhysicalStores.getObjectFromArrayByName(
        storeAttributes,
        `close${dayOfWeek}`
      )?.value

      return {
        dayName: dayOfWeek,
        storeHours: `${openHour} - ${closeHour}`,
      }
    }).filter((dayOfWeekWithStoreHours) => dayOfWeekWithStoreHours.storeHours !== ' - ')

    const daysOfWeekGroupedByStoreHours = groupBy(daysOfWeekWithStoreHours, 'storeHours')
    const groupedDaysOfWeekKeys = Object.keys(daysOfWeekGroupedByStoreHours)
    const groupedDaysOfWeekValues = Object.values(daysOfWeekGroupedByStoreHours)

    if (groupedDaysOfWeekKeys.length === 1) {
      return [
        `${PhysicalStores.getFormattedDayName(
          head(daysOfWeekWithStoreHours)?.dayName ?? ''
        )}-${PhysicalStores.getFormattedDayName(last(daysOfWeekWithStoreHours)?.dayName ?? '')}: ${
          groupedDaysOfWeekKeys[0]
        }`,
      ]
    }

    return groupedDaysOfWeekValues.map((groupOfDays: any) => {
      if (groupOfDays.length === 1) {
        return `${PhysicalStores.getFormattedDayName(groupOfDays[0].dayName)}: ${
          groupOfDays[0].storeHours
        }`
      }

      let dayOfWeekIndex = 0
      let nextDayOfWeekIndex = 1
      let isDayInsideSequence = false
      let composedMessageOfDaysAndHours = ''

      for (let iterator = 0; iterator < groupOfDays.length - 1; iterator++) {
        dayOfWeekIndex = DAYS_OF_WEEK.indexOf(groupOfDays[iterator].dayName)
        nextDayOfWeekIndex = DAYS_OF_WEEK.indexOf(groupOfDays[iterator + 1].dayName)

        if (dayOfWeekIndex + 1 === nextDayOfWeekIndex) {
          if (isDayInsideSequence) {
            continue
          } else {
            isDayInsideSequence = true
            composedMessageOfDaysAndHours += `${PhysicalStores.getFormattedDayName(
              DAYS_OF_WEEK[dayOfWeekIndex]
            )}-`
          }
        } else {
          if (isDayInsideSequence) {
            isDayInsideSequence = false
          }
          composedMessageOfDaysAndHours += `${PhysicalStores.getFormattedDayName(
            DAYS_OF_WEEK[dayOfWeekIndex]
          )}, `
        }
      }

      composedMessageOfDaysAndHours += `${PhysicalStores.getFormattedDayName(
        (last(groupOfDays) as any)?.dayName ?? ''
      )}: ${groupOfDays[0].storeHours}`

      return composedMessageOfDaysAndHours
    })
  }
  // TODO: Remove this method
  static removeMobilePanelClass = function () {
    const htmlTag = document.getElementsByTagName('html')
    htmlTag[0].classList.remove(MOBILE_PANELS_OPEN)
  }

  //TODO: specify storeType type
  private static getMarkerIcon = (
    searchType: PhysicalStoreType,
    storeType: string | undefined,
    baseUrl = ''
  ) => {
    if (!storeType) {
      return undefined
    }
    switch (searchType) {
      case PhysicalStoreType.STORE_LOCATOR:
        return PhysicalStores.getMarkerIconForStoreLocator(storeType, baseUrl)
      case PhysicalStoreType.CLICK_AND_COLLECT:
      default:
        return PhysicalStores.getMarkerIconForClickAndCollect(storeType)
    }
  }
  // TODO: Extract storeLocatorStoreType into enum
  static getMarkerIconForStoreLocator = (storeType: string, baseUrl = '') => {
    switch (storeType) {
      case RAYBAN_STORES:
        return baseUrl ? `${baseUrl}${MARKER_RED_URL}` : `${MARKER_RED_URL}`
      case PREMIUM:
        return baseUrl ? `${baseUrl}${MARKER_BLACK_URL}` : `${MARKER_BLACK_URL}`
      case CERTIFIED:
      default:
        return baseUrl ? `${baseUrl}${MARKER_GREY_URL}` : `${MARKER_GREY_URL}`
    }
  }

  private static getMarkerIconForClickAndCollect = (distributionChannel: string) => {
    switch (distributionChannel) {
      case DistributionChannel.RAY_BAN:
        return MARKER_RED_URL
      case DistributionChannel.SGH:
        return MARKER_BLACK_SGH
      case DistributionChannel.LENS_CRAFTERS:
        return MARKER_BLACK_LENS_CRAFTERS
      case DistributionChannel.UPS:
        return MARKER_UPS
      default:
        return MARKER_BLACK_URL
    }
  }

  static mapDistributionChannel = (distributionChannel: string) => {
    return distributionChannel === DistributionChannel.SGH ? SUNGLASS_HUT : distributionChannel
  }

  static filterStoresList = function (storeList: PhysicalStore[]) {
    const filteredStoreList = storeList.filter((store) => {
      return store?.isStockAvailable
    })
    return filteredStoreList
  }

  static filterOnlyBopsStores = (storeList: PhysicalStore[]) => {
    if (!window.productsJson) {
      return []
    }
    const productsArray = Object.keys(window.productsJson).map((key) => window.productsJson[key])
    const isAnyProductOutOfStock = productsArray.some((product) => product.Stock == '0')
    if (isAnyProductOutOfStock) {
      const filteredStoreList = storeList.filter((store) => {
        const isProductInStock = productsArray.every(
          (product) =>
            store?.availableStock &&
            store.omniLimitedInvThreshold &&
            +store?.availableStock - +store?.omniLimitedInvThreshold >= +product?.Units
        )
        return store?.isBops && isProductInStock
      })
      return filteredStoreList
    } else {
      return storeList
    }
  }

  private static isStoreBopsOrBossByType = (store: WcsPhysicalStore, type: string) => {
    const attribute = PhysicalStores.getObjectFromArrayByName(store?.attributes, type)
    const isStoreBopsOrBossByType = attribute?.value === YES || attribute?.value === ONE
    return isStoreBopsOrBossByType
  }

  private static isEUStoreBopsOrBossByType = (store: WcsEUStore, type: string) => {
    const attribute = PhysicalStores.getObjectFromArrayByName(store?.Attribute, type)
    const isStoreBopsOrBossByType = attribute?.value === YES || attribute?.value === ONE
    return isStoreBopsOrBossByType
  }

  private static isStoreOpen = (
    openingHours: any,
    closingHours: any,
    timezone: string,
    closeHoursThreshold = 0
  ) => {
    const localeDate = new Date()
    const storeDateMoment = RbDate.fromZonedTimeDate(localeDate)
    const openDate = `${RbDate.formatDate(storeDateMoment, YYYY_MM_DD_LOWER)} ${openingHours}`
    const openingHourMoment = RbDate.fromZonedTimeDate(openDate)
    const closedDate = `${RbDate.formatDate(openingHourMoment, YYYY_MM_DD_LOWER)} ${closingHours}`
    const closingHourMoment = RbDate.fromZonedTimeDate(closedDate)

    const closingHourMomentSub = RbDate.subDate(closingHourMoment, { hours: closeHoursThreshold })
    const isOpen = RbDate.isWithinIntervalDate(closingHourMomentSub, openingHourMoment)
    return isOpen
  }

  private static isStoreAboutToOpen = (openingHours: any, timezone: string) => {
    const localeDate = new Date()
    const storeDateMoment = RbDate.fromZonedTimeDate(localeDate)
    const localeDayOfWeek = (RbDate.getDayNumber(localeDate) + 6) % 7
    const storeDayOfWeek = (RbDate.getDayNumber(storeDateMoment) + 6) % 7

    if (localeDayOfWeek !== storeDayOfWeek) {
      return false
    }
    const openingHourDate = RbDate.parseDate(
      `${RbDate.formatDate(storeDateMoment, 'yyyy-MM-dd')} ${openingHours}`,
      'yyyy-MM-dd HH:mm'
    )

    return RbDate.isBeforeDate(storeDateMoment, openingHourDate)
  }

  static mapDistributionChannelPriority = (value: string) => {
    switch (value) {
      case DistributionChannel.RAY_BAN:
        return 0
      case DistributionChannel.SGH:
        return 1
      case DistributionChannel.LENS_CRAFTERS:
        return 2
      default:
        return 3
    }
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  static sortStores = (stores: PhysicalStore[], isAvailableToday: Function, isPdp: boolean) => {
    const bopsStores = stores.filter((store) => {
      return (
        store?.isBops &&
        store?.availableStock &&
        isAvailableToday(+store?.availableStock, store?.omniLimitedInvThreshold)
      )
    })
    bopsStores.sort(PhysicalStores.sortByDistance)
    bopsStores.sort(PhysicalStores.sortByDistributor)
    let findStores: PhysicalStore[] = []
    if (isPdp) {
      findStores = stores.filter((store) => {
        return (
          store?.isFindInStore &&
          store.availableStock &&
          isAvailableToday(+store.availableStock, store.omniLimitedInvThreshold)
        )
      })
      findStores.sort(PhysicalStores.sortByDistance)
    }
    let bossStores: PhysicalStore[] = []
    if (!isPdp) {
      bossStores = stores.filter((store) => {
        return store?.isBoss
      })
      bossStores.sort(PhysicalStores.sortByDistance)
    }
    const mergedStores = [...bopsStores, ...findStores, ...bossStores]
    const uniqueStores = uniqBy(mergedStores, (store: PhysicalStore) => store.uniqueID)
    return uniqueStores
  }

  private static sortByDistance = (a: PhysicalStore, b: PhysicalStore) => {
    return a.distance - b.distance
  }

  private static sortByDistributor = (a: PhysicalStore, b: PhysicalStore) => {
    if (
      a.distributionChannel === DistributionChannel.RAY_BAN &&
      b.distributionChannel !== DistributionChannel.RAY_BAN
    ) {
      return -1
    }
    if (
      a.distributionChannel !== DistributionChannel.RAY_BAN &&
      b.distributionChannel === DistributionChannel.RAY_BAN
    ) {
      return 1
    }
    return 0
  }

  static getPickupStoreKey = () => {
    return 'rayban.' + storeId + '.' + langId + '.pickupStore'
  }

  static getTimeClose = (closeHours: string[]) => {
    const todayDateStore = RbDate.formatDate(new Date(), YYYY_MM_DD_LOWER)
    const dayOfWeek = RbDate.getDayNumber(RbDate.getTimeDate())

    if (closeHours && closeHours.length) {
      //TODO: investigate if we can remove this logic and return directly the closing hour from array
      const closeDateTimeString = new Date(`${todayDateStore} ${closeHours[dayOfWeek]}`)
      const closedDateTime = RbDate.fromZonedTimeDate(RbDate.getTimeDate(closeDateTimeString))
      return RbDate.formatDate(closedDateTime, HHmm)
    }
    return ''
  }

  static getTimeOpen = (openHours: string[]) => {
    const todayDateStore = RbDate.formatDate(new Date(), YYYY_MM_DD_LOWER)
    const dayOfWeek = RbDate.getDayNumber(RbDate.getTimeDate())

    if (openHours && openHours.length && openHours[dayOfWeek]) {
      //TODO: investigate if we can remove this logic and return directly the opening hour from array
      const openDateTimeString = new Date(`${todayDateStore} ${openHours[dayOfWeek]}`)
      const openedDateTime = RbDate.fromZonedTimeDate(RbDate.getTimeDate(openDateTimeString))
      return RbDate.formatDate(openedDateTime, HHmm)
    }
    return ''
  }

  static isStoreOpened = (openHours: any, closeHours: any, timeZone: string) => {
    return openHours && closeHours && PhysicalStores.isStoreOpen(openHours, closeHours, timeZone)
  }

  private static getSFSHours = (
    storeAttributes: WcsPhysicalStoreAttribute[],
    deliveryOption: SfsDeliveryOptionType
  ) => {
    return {
      start: PhysicalStores.getObjectFromArrayByName(
        storeAttributes,
        `${deliveryOption}.deliverBetweenStart`
      )?.value,
      end: PhysicalStores.getObjectFromArrayByName(
        storeAttributes,
        `${deliveryOption}.deliverBetweenEnd`
      )?.value,
      delivWindowExpiresAt: PhysicalStores.getObjectFromArrayByName(
        storeAttributes,
        `${deliveryOption}.delivWindowExpiresAt`
      )?.value,
      pickUpAfter: PhysicalStores.getObjectFromArrayByName(
        storeAttributes,
        `${deliveryOption}.pickupAfter`
      )?.value,
    }
  }

  private static getSFSOptions = (
    storeAttributes: WcsPhysicalStoreAttribute[]
  ): ShipFromStoreOption[] | undefined => {
    const SFSAttribute = PhysicalStores.getObjectFromArrayByName(storeAttributes, 'shfrst')?.value
    const SFSAvailable = SFSAttribute === YES || SFSAttribute === ONE
    if (SFSAvailable) {
      const isSameDayAvailable = PhysicalStores.getObjectFromArrayByName(
        storeAttributes,
        SfsDeliveryOptionType.SAME_DAY_DELIVERY
      )?.value
      const isNextDayAvailable = PhysicalStores.getObjectFromArrayByName(
        storeAttributes,
        SfsDeliveryOptionType.NEXT_DAY_DELIVERY
      )?.value
      const days = []

      if (isSameDayAvailable === YES) {
        days.push({
          name: SfsDeliveryOptionType.SAME_DAY_DELIVERY,
          hours: PhysicalStores.getSFSHours(
            storeAttributes,
            SfsDeliveryOptionType.SAME_DAY_DELIVERY
          ),
          scheduledDeliveryDate: RbDate.fromZonedTimeDate(),
        })
      }

      if (isNextDayAvailable === YES) {
        days.push({
          name: SfsDeliveryOptionType.NEXT_DAY_DELIVERY,
          hours: PhysicalStores.getSFSHours(
            storeAttributes,
            SfsDeliveryOptionType.NEXT_DAY_DELIVERY
          ),
          scheduledDeliveryDate: RbDate.fromZonedTimeDate(),
        })
      }

      return days
    }
    return undefined
  }

  static normalizeEUPhysicalStore = (store: WcsEUStore) => {
    const storeAttributes = store?.Attribute ?? []
    const hoursArray = PhysicalStores.setHoursToArray(storeAttributes)
    const hoursArrayCopy = JSON.parse(JSON.stringify(hoursArray))
    // IT IS MANDATORY TO USE hoursArrayCopy BECAUSE groupByWeekDays IS ALTERING THE ACTUAL hoursArray
    const groupedWeekDays = PhysicalStores.groupByWeekDays(hoursArrayCopy[0], hoursArrayCopy[1])
    const timeZone =
      PhysicalStores.getObjectFromArrayByName(storeAttributes, TIME_ZONE)?.value ?? ''
    const openingHours = PhysicalStores.getTimeOpen(hoursArray[0])
    const searchType: PhysicalStoreType = PhysicalStoreType.CLICK_AND_COLLECT

    const address = store.addressLine[0]
    const bestCallingTime = PhysicalStores.buildBestCallingTime(groupedWeekDays)
    const city = store.city
    const closingHours = PhysicalStores.getTimeClose(hoursArray[1])
    const country = store.country
    const distance = +store.distance
    const distributionChannel = PhysicalStores.getObjectFromArrayByName(
      storeAttributes,
      DISTRIBUTION_CHANNEL
    )?.value
    const externalIdentifier = store.storeName
    const hereYouCanFind: string[] = []
    const isStockAvailable = false
    const isAvailableToday =
      PhysicalStores.isStoreOpen(
        openingHours,
        closingHours,
        timeZone,
        STORE_CLOSING_HOUR_THRESHOLD
      ) || PhysicalStores.isStoreAboutToOpen(openingHours, timeZone)
    const isBops = PhysicalStores.isEUStoreBopsOrBossByType(store, BOPSCNC) && isStockAvailable
    const isBoss = PhysicalStores.isEUStoreBopsOrBossByType(store, BOSSSTS)
    const isFindInStore = false
    const isLimitedQuantity = false
    const isOpen = PhysicalStores.isStoreOpened(openingHours, closingHours, timeZone)
    const latitude = +store.latitude
    const longitude = +store.longitude
    const markerIcon = PhysicalStores.getMarkerIcon(searchType, distributionChannel, '')
    const omniLimitedInvThreshold = ''
    const postalCode = store.postalCode
    const services = undefined
    const sfsOptions = undefined
    const shippingId = PhysicalStores.getObjectFromArrayByName(storeAttributes, SHIPPING_ID)?.value
    const shipToName1 = PhysicalStores.getObjectFromArrayByName(
      storeAttributes,
      SHIP_TO_NAME_1
    )?.value
    const shipToOrt1 = PhysicalStores.getObjectFromArrayByName(
      storeAttributes,
      SHIP_TO_ORT_1
    )?.value
    const shipToStras = PhysicalStores.getObjectFromArrayByName(
      storeAttributes,
      SHIP_TO_STRAS
    )?.value
    const state = country
    const storeBrand = distributionChannel
      ? PhysicalStoresEU.mapStoreBrandForEU(distributionChannel)
      : undefined
    const storeName = store.Description[0].displayStoreName
    const storeSubmitted = false
    const storeType = distributionChannel
    const telephone = store.telephone1
    const uniqueID = store.uniqueID
    const weekScheduleArray = PhysicalStores.getWeekScheduleArray(storeAttributes)
    const weekScheduleTable = PhysicalStores.getWeekScheduleTable(groupedWeekDays)

    const normalizedEUPhysicalStore: PhysicalStore = {
      address,
      bestCallingTime,
      city,
      closingHours,
      country,
      distance,
      distributionChannel,
      externalIdentifier,
      hereYouCanFind,
      hoursArray,
      isAvailableToday,
      isBops,
      isBoss,
      isFindInStore,
      isLimitedQuantity,
      isOpen,
      isStockAvailable,
      latitude,
      longitude,
      markerIcon,
      omniLimitedInvThreshold,
      openingHours,
      postalCode,
      services,
      sfsOptions,
      shippingId,
      shipToName1,
      shipToOrt1,
      shipToStras,
      state,
      storeBrand,
      storeName,
      storeSubmitted,
      storeType,
      telephone,
      timeZone,
      uniqueID,
      weekScheduleArray,
      weekScheduleTable,
    }

    return normalizedEUPhysicalStore
  }

  static normalizeUPSPhysicalStore = (store: WcsUPSStore, shippingId: string) => {
    const openHoursAttributesArray = PhysicalStoresEU.getUPSAttributesForHoursArray(
      store.OperatingHours.StandardHours[0].DayOfWeek
    )
    const hoursArray = PhysicalStores.setHoursToArray(openHoursAttributesArray)
    const hoursArrayCopy = JSON.parse(JSON.stringify(hoursArray))
    //IT IS MANDATORY TO USE hoursArrayCopy BECAUSE groupByWeekDays IS ALTERING THE ACTUAL hoursArray
    const groupedWeekDays = PhysicalStores.groupByWeekDays(hoursArrayCopy[0], hoursArrayCopy[1])
    const timeZone = PhysicalStoresEU.getTimeZoneForUPSStore()
    const openingHours = PhysicalStores.getTimeOpen(hoursArray[0])
    const searchType: PhysicalStoreType = PhysicalStoreType.CLICK_AND_COLLECT

    const address = store.AddressKeyFormat.AddressLine
    const bestCallingTime = PhysicalStores.buildBestCallingTime(groupedWeekDays)
    const city = store.AddressKeyFormat.PoliticalDivision2
    const closingHours = PhysicalStores.getTimeClose(hoursArray[1])
    const country = store.AddressKeyFormat.CountryCode
    const distance = store.Distance.Value
    const distributionChannel = UPS
    const externalIdentifier = ''
    const hereYouCanFind: string[] = []
    const isAvailableToday =
      !!PhysicalStores.isStoreOpen(
        openingHours,
        closingHours,
        timeZone,
        STORE_CLOSING_HOUR_THRESHOLD
      ) || !!PhysicalStores.isStoreAboutToOpen(openingHours, timeZone)
    const isBops = false
    const isBoss = true
    const isFindInStore = false
    const isLimitedQuantity = false
    const isOpen = PhysicalStores.isStoreOpened(openingHours, closingHours, timeZone)
    const isStockAvailable = false
    const latitude = store.Geocode.Latitude
    const longitude = store.Geocode.Longitude
    const markerIcon = PhysicalStores.getMarkerIcon(searchType, distributionChannel, '')
    const omniLimitedInvThreshold = ''
    const postalCode = store.AddressKeyFormat.PostcodePrimaryLow.split('-')[1]
    const services = undefined
    const sfsOptions = undefined
    const shipToName1 = store.AddressKeyFormat.ConsigneeName
    const shipToOrt1 = city
    const shipToStras = address
    const state = country
    const storeBrand = UPS
    const storeName = store.AddressKeyFormat.ConsigneeName
    const storeSubmitted = false
    const storeType = UPS
    const telephone = store.PhoneNumber ?? ''
    const uniqueID = String(store.LocationID)
    const weekScheduleArray: string[] =
      PhysicalStores.getWeekScheduleArray(openHoursAttributesArray)
    const weekScheduleTable = PhysicalStores.getWeekScheduleTable(groupedWeekDays)

    const normalizedUPSPhysicalStore: PhysicalStore = {
      address,
      bestCallingTime,
      city,
      closingHours,
      country,
      distance,
      distributionChannel,
      externalIdentifier,
      hereYouCanFind,
      hoursArray,
      isAvailableToday,
      isBops,
      isBoss,
      isFindInStore,
      isLimitedQuantity,
      isOpen,
      isStockAvailable,
      latitude,
      longitude,
      markerIcon,
      omniLimitedInvThreshold,
      openingHours,
      postalCode,
      services,
      sfsOptions,
      shippingId,
      shipToName1,
      shipToOrt1,
      shipToStras,
      state,
      storeBrand,
      storeName,
      storeSubmitted,
      storeType,
      telephone,
      timeZone,
      uniqueID,
      weekScheduleArray,
      weekScheduleTable,
    }

    return normalizedUPSPhysicalStore
  }
}
